import { FC, useCallback, useState } from 'react'
import { Actions, TransferOwnershipWrapper } from '../userProfile.styles'
import { Box, CircularProgress, Typography } from '@mui/material'
import { Form, Formik } from 'formik'
import { UserServiceV4 } from 'services'

/* Utils */
import {
  closeModal,
  modalPropsSelector,
  ModalType,
  openModal,
  setModalProps
} from '../../../../../../store/modals/modalSlice'
import { useAppDispatch, useAppSelector } from '../../../../../../store/hooks'
import { closeToast, openToast, ToastVariant } from '../../../../../../store/toasts/toastSlice'

/* Components */
import { ThemeButton } from '../../../../buttons'
import ControlledTextField from '../../../../form-components/ControlledTextField'

const TransferOwnership: FC = () => {
  const [helperText, setHelperText] = useState('')
  const dispatch = useAppDispatch()
  const modalProps = useAppSelector(modalPropsSelector)

  const handleClose = () => {
    dispatch(openModal({ type: ModalType.userProfile, props: modalProps.props }))
  }

  const fetchUser = useCallback(async () => {
    try {
      return await UserServiceV4.getMe()
    } catch (err) {
      console.error(err)
    }
  }, [])

  const handleConfirm = async (values: any) => {
    if (!modalProps.props) return
    if (!modalProps.props.user) return

    let id = ''

    const owner = values.owner ? values.owner : modalProps.props.user.email || ''
    try {
      const found = await UserServiceV4.getUserByEmail(owner)

      if (found) {
        id = found.id
        setHelperText('')
      } else {
        setHelperText('User not found')
      }
    } catch (err) {
      console.error(err)
    }

    if (!id) {
      return
    }

    try {
      const modalRole = modalProps.props.newRole || ''
      const newRole = values.owner ? modalRole : 'admin'
      await UserServiceV4.transferOwnership(id, newRole)
      const refetchedUser = await fetchUser()

      dispatch(
        openToast({
          variant: ToastVariant.success,
          props: {
            text: 'Role Added',
            description: `Ownership has been revoked from your account and granted to ${values.owner}, you are now ${
              modalRole === 'demo' ? '' : 'a'
            }${modalRole === 'viewer' || modalRole === 'admin' ? 'n' : ''} ${modalRole}.`
          }
        })
      )
      dispatch(
        setModalProps({
          open: false,
          props: {
            ...modalProps.props,
            user: values.owner
              ? {
                  ...modalProps.props.user,
                  role: newRole
                }
              : refetchedUser
          }
        })
      )
      dispatch(closeModal())
    } catch (e) {
      console.error(e)
      dispatch(
        openToast({
          variant: ToastVariant.error,
          props: {
            text: 'Failed changing role',
            description: (e as any).response?.data?.message ?? 'There was a problem while transferring ownership.'
          }
        })
      )
    } finally {
      setTimeout(() => {
        dispatch(closeToast())
      }, 5000)
      setHelperText('')
    }
  }

  if (!modalProps.props || !modalProps.props.user) {
    return <div>No user found.</div>
  }
  return (
    <Formik initialValues={{}} onSubmit={handleConfirm}>
      {({ isSubmitting, isValid }) => (
        <Form autoComplete="off">
          <TransferOwnershipWrapper>
            <Box className="content">
              <Typography className="title">Are you sure you want to reassign ownership?</Typography>
              <Typography className="text">
                Only one user can be the workspace owner at a time. You will be demoted to an Administrator.
              </Typography>
              {modalProps.props?.showTransferInput && (
                <ControlledTextField
                  name="owner"
                  label="Transfer Ownership To"
                  className="account"
                  error={helperText.length > 0}
                  helperText={helperText}
                />
              )}
            </Box>
            <Actions>
              <ThemeButton themevariant="tertiary" type="button" onClick={handleClose}>
                Cancel
              </ThemeButton>
              <ThemeButton
                themevariant="primary"
                type="submit"
                disabled={isSubmitting || !isValid}
                endIcon={isSubmitting && <CircularProgress size="20px" />}
              >
                Confirm
              </ThemeButton>
            </Actions>
          </TransferOwnershipWrapper>
        </Form>
      )}
    </Formik>
  )
}

export default TransferOwnership
